import { Injectable } from '@angular/core';
import { WorkItem } from 'common/workitems/interfaces/workitem-base.interface';
import { pull } from 'lodash';
import * as moment from 'moment';
import { WorkItemPool } from 'common/workitems/workitem-pool';
import { Subject } from 'rxjs';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { EmailList } from 'common/workitems/lists/email-list';
import { CoolLocalStorage } from '@angular-cool/storage';
import { LogoutSubscriber } from 'shared/services/subscribers/logout-subscriber.service';
import { bcSessionKeys } from 'common/common/constants/session-keys';
import { BcConfigService } from 'common/config/provider';
import { ChatItem } from 'common/workitems/interfaces/chat-item.interface';
import { TextChatItem } from 'common/workitems/interfaces/text-chat.interface';
import { EmailItem } from 'common/workitems/interfaces/email-item.interface';

const workItemTypesOrder = {
  [WorkItemTypes.LENS]: 1,
  [WorkItemTypes.CHAT_PIN_INVITE]: 2,
  [WorkItemTypes.CHAT]: 3,
  [WorkItemTypes.FACEBOOK]: 4,
  [WorkItemTypes.ASYNC_MESSAGE]: 4,
  [WorkItemTypes.SMS]: 4,
  [WorkItemTypes.COMPOSE_EMAIL]: 5,
  [WorkItemTypes.EMAIL]: 6
};

let MAX_OPEN_WORKITEM_COUNT = 1;

type HasCreatedProperty = { Created?: string };

@Injectable()
export class WorkItemStateHandler {
  private _openedWorkItems: string[];
  private _focusedWorkItemId: string | null = null;
  private readonly _focusedWorkItemIdSubject = new Subject<string>();
  private readonly _openedWorkItemsChangedSubject = new Subject<void>();
  readonly workItemFocused$ = this._focusedWorkItemIdSubject.asObservable();
  readonly openedWorkItemsChanged$ = this._openedWorkItemsChangedSubject.asObservable();

  constructor(private readonly appEmailList: EmailList,
              readonly bcConfig: BcConfigService,
              private readonly localStorageService: CoolLocalStorage,
              private readonly logoutSubscriber: LogoutSubscriber,
              private readonly workItemPool: WorkItemPool) {

    if (bcConfig && bcConfig.current.features.multiLane) {
      MAX_OPEN_WORKITEM_COUNT = 6;
    }

    let loadedStates = this.localStorageService.getItem(bcSessionKeys.storage.openWorkItemList);
    if (loadedStates) {
      this._openedWorkItems = JSON.parse(loadedStates);
    } else {
      this._openedWorkItems = [];
    }

    this._focusedWorkItemId = this.localStorageService.getItem(bcSessionKeys.storage.focusedWorkItem);
    this.logoutSubscriber.subscribe(this.resetList.bind(this));
  }

  resetList(): void {
    this._openedWorkItems = [];
    this._openedWorkItemsChangedSubject.next();
    this._focusedWorkItemId = null;
    this._saveState();
  }

  private _orderOpenWorkItems(): void {
    this._openedWorkItems.sort(this._workItemSorter.bind(this));
  }

  private _workItemSorter(firstItemIndex: string, secondItemIndex: string): number {
    const firstItem: WorkItem = this._getWorkItemById(firstItemIndex);
    const secondItem: WorkItem = this._getWorkItemById(secondItemIndex);
    if (!firstItem || !secondItem) {
      return 0;
    }

    if (workItemTypesOrder[firstItem.type] !== workItemTypesOrder[secondItem.type]) {
      return workItemTypesOrder[firstItem.type] - workItemTypesOrder[secondItem.type];
    }

    switch (firstItem.type) {
      case WorkItemTypes.CHAT:
        return this.sortChatItems(<ChatItem>firstItem, <ChatItem>secondItem);
      case WorkItemTypes.FACEBOOK:
      case WorkItemTypes.ASYNC_MESSAGE:
      case WorkItemTypes.SMS:
        return this.textChatComparator(<TextChatItem>firstItem, <TextChatItem>secondItem);
      case WorkItemTypes.EMAIL:
        return this.emailListOrderMatcher(<EmailItem>firstItem, <EmailItem>secondItem);
      default:
        return 0;
    }
  }

  // There's no Chat Item List yet
  public sortChatItems(workItem1: ChatItem, workItem2: ChatItem): number {
    return WorkItemStateHandler.compareObjectsByCreatedDateDesc(workItem1, workItem2);
  }

  public static compareObjectsByCreatedDateDesc(obj1: HasCreatedProperty, obj2: HasCreatedProperty): number {
    const date1 = moment(String(obj1.Created));
    const date2 = moment(String(obj2.Created));

    if (date1.isAfter(date2)) {
      return -1;
    } else if (date1.isSame(date2)) {
      return 0;
    }
    return 1;
  }

  public static compareObjectsByCreatedDateAsc(obj1: HasCreatedProperty, obj2: HasCreatedProperty): number {
    return -(WorkItemStateHandler.compareObjectsByCreatedDateDesc(obj1, obj2));
  }

  // There's no TextChat Item List yet
  public textChatComparator(textChat1: TextChatItem, textChat2: TextChatItem): number {
    return this._compareTextChatByCreated(textChat1, textChat2);
  }

  private _compareTextChatByCreated(textChat1: TextChatItem, textChat2: TextChatItem): number {
    if (textChat1.Created === textChat2.Created) {
      return 0;
    } else if (textChat1.Created < textChat2.Created) {
      return -1;
    }
    return 1;
  }

  emailListOrderMatcher(emailItem1: EmailItem, emailItem2: EmailItem): number {
    return this.appEmailList.emailWorkItemList.indexOf(emailItem1) - this.appEmailList.emailWorkItemList.indexOf(emailItem2);
  }

  private _makeSpaceForNewItem(): void {
    let collapsibleElements = this._getCollapsibleElements();
    while (collapsibleElements.length > MAX_OPEN_WORKITEM_COUNT - 1) {
      this.closeWorkItem(collapsibleElements[collapsibleElements.length - 1].id);
      collapsibleElements = this._getCollapsibleElements();
    }
  }

  private _getCollapsibleElements(): WorkItem[] {
    return this.getOpenWorkItems().filter(item => item.isCollapsible);
  }

  openWorkItem(workItemId: string): void {
    if (this._openedWorkItems.includes(workItemId)) {
      return;
    }
    const workItem = this._getWorkItemById(workItemId);
    if (workItem.isCollapsible && this._getCollapsibleElements().length > MAX_OPEN_WORKITEM_COUNT - 1) {
      this._makeSpaceForNewItem();
    }
    this._openedWorkItems.push(workItemId);
    this._openedWorkItemsChangedSubject.next();
    this._orderOpenWorkItems();
    this._saveState();
  }

  closeWorkItem(workItemId: string): void {
    if (this._focusedWorkItemId === workItemId) {
      if (this.getNextWorkItemId()) {
        this.selectNextWorkItem();
      } else {
        this.selectPreviousWorkItem();
      }
    }

    pull(this._openedWorkItems, workItemId);
    this._openedWorkItemsChangedSubject.next();

    // Clear focus if this was the last item
    if (this._openedWorkItems.length === 0) {
      this.unFocusWorkItem();
    }

    this._saveState();
  }

  removeWorkItem(workItemId: string): void {
    this.closeWorkItem(workItemId);
    this.workItemPool.removeWorkItemById(workItemId);
  }

  focusWorkItem(workItemId: string): void {
    this._focusedWorkItemId = workItemId;
    if (!this._openedWorkItems.includes(this._focusedWorkItemId)) {
      this.unFocusWorkItem();
      return this.selectNextWorkItem();
    }

    this._focusedWorkItemIdSubject.next(workItemId);

    this._saveState();
  }

  unFocusWorkItem(): void {
    this._focusedWorkItemId = null;

    this._saveState();
  }

  isFocused(workItemId: string): boolean {
    const focused = this.getFocusedWorkItem();
    return (focused && focused.id === workItemId);
  }

  isOpened(workItemId: string): boolean {
    return this.getOpenWorkItems().some(wi => wi.id === workItemId);
  }

  getOpenWorkItems(): WorkItem[] {
    return this._openedWorkItems.map(workItemId => this._getWorkItemById(workItemId)).filter(item => item);
  }

  getFocusedWorkItem(): WorkItem | null {
    if (!this._focusedWorkItemId) {
      return null;
    }
    if (!this._openedWorkItems.includes(this._focusedWorkItemId)) {
      return null;
    }
    return this._getWorkItemById(this._focusedWorkItemId);
  }

  selectPreviousWorkItem(): void {
    const workItems = this.getOpenWorkItems();
    const indexOfSelectedWorkItem = workItems.indexOf(this.getFocusedWorkItem());

    let previousWorkItemIndex = indexOfSelectedWorkItem - 1;

    if (previousWorkItemIndex < 0) {
      previousWorkItemIndex = 0;
    }

    if (this._openedWorkItems.length > 0) {
      this.focusWorkItem(workItems[previousWorkItemIndex].id);
    } else {
      this.unFocusWorkItem();
    }
  }

  selectNextWorkItem(): void {
    const workItems = this.getOpenWorkItems();
    const indexOfSelectedWorkItem = workItems.indexOf(this.getFocusedWorkItem());

    let nextWorkItemIndex = indexOfSelectedWorkItem + 1;

    if (nextWorkItemIndex > workItems.length - 1) {
      nextWorkItemIndex = workItems.length - 1;
    }

    if (nextWorkItemIndex > -1) {
      this.focusWorkItem(this.getOpenWorkItems()[nextWorkItemIndex].id);
    } else {
      this.unFocusWorkItem();
    }
  }

  getPreviousWorkItemId(): string | null {
    const indexOfSelectedWorkItem = this.getOpenWorkItems().indexOf(this.getFocusedWorkItem());
    const previousWorkItemIndex = indexOfSelectedWorkItem - 1;

    if (previousWorkItemIndex < 0) {
      return null;
    }

    return this.getOpenWorkItems()[previousWorkItemIndex].id;
  }

  getNextWorkItemId(): string | null {
    const indexOfSelectedWorkItem = this.getOpenWorkItems().indexOf(this.getFocusedWorkItem());
    const nextWorkItemIndex = indexOfSelectedWorkItem + 1;

    if (nextWorkItemIndex > this.getOpenWorkItems().length - 1) {
      return null;
    }

    return this.getOpenWorkItems()[nextWorkItemIndex].id;
  }

  get openedWorkItemIds(): string[] {
    return this._openedWorkItems;
  }

  private _getWorkItemById(workItemId: string): WorkItem {
    return this.workItemPool.getWorkItemById(workItemId);
  }

  private _saveState(): void {
    this.localStorageService.setItem(bcSessionKeys.storage.openWorkItemList, JSON.stringify(this._openedWorkItems));
    this.localStorageService.setItem(bcSessionKeys.storage.focusedWorkItem, this._focusedWorkItemId);
  }
}
